/* eslint-disable react-hooks/exhaustive-deps */
import { useTheme } from "@emotion/react";
import { Box, CircularProgress, Divider, Typography, useMediaQuery } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardService from "../services/dashboardService";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import TimeDropdown from "../components/dropdowns/timeDropdown";
import BranchDropdown from "../components/dropdowns/branchDropdown";
import { AuthContext } from "../provider/authProvider";
import PostStatusBarChart from "../components/dashboard/postStatusBarChart";
import TopThreeCard from "../components/dashboard/topThreeCard";
import LineChartBox from "../components/dashboard/lineChart";
import DashboardCard from "../components/dashboard/dashboardCard";

const Dashboard = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedBranch, setSelectedBranch] = useState(null);

    const [data, setData] = useState(null);
    const { user } = useContext(AuthContext);
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');

    const [selectedTime, setSelectedTime] = useState({
        time: 'today',
        value: t('today')
    });

    const [isLoading, setIsLoading] = useState(false);

    const fetchDashboard = async () => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                branchId: selectedBranch?.id,
                ...selectedTime
            };

            const data = await DashboardService.fetchDashboard(filters);
            setData(data);

        } catch (error) {
            console.error('Error fetching dashboard:', error);
        } finally {
            setIsLoading(false);
        }
    };


    const resetFilter = () => {
        setSelectedTime(null);
    };


    useEffect(() => {
        fetchDashboard();
    }, [selectedBranch, selectedTime]);


    return (
        <Box sx={{
            p: isTablet ? 1 : 2,
            backgroundColor: theme.palette.secondary.main,
            width: '100%',
        }}>

            {/* Filter row */}
            {
                isTablet ?
                    <Box
                        sx={{
                            mb: 2,
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            height: 50
                        }}>
                            <Box sx={{ px: 2 }}>
                                <FilterAltOutlinedIcon />
                            </Box>
                            {allBranchPermission && <Divider orientation="vertical" />}
                            {allBranchPermission && <Box sx={{
                                alignContent: 'center',
                                height: 50,
                                width: '70%'
                            }}>
                                <BranchDropdown
                                    isFilter={true}
                                    selectedBranch={selectedBranch}
                                    setSelectedBranch={setSelectedBranch}
                                />
                            </Box>}
                            <Divider orientation="vertical" />

                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: theme.palette.text.red,
                                    px: 2,
                                    cursor: 'pointer',
                                }}
                                onClick={resetFilter}
                            >
                                <ReplayOutlinedIcon />
                                <Box width={3}></Box>
                                <Typography variant="subtitle1">{t('resetFilter')}</Typography>
                            </Box>
                        </Box>

                        <Divider />
                        <Box width={'100%'} height={50}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                    </Box>
                    :
                    <Box
                        sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={allBranchPermission ? '50%' : '70%'}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                        {allBranchPermission && <Divider orientation="vertical" />}
                        {allBranchPermission && <Box sx={{
                            alignContent: 'center',
                            height: 50,
                            width: '30%'
                        }}>
                            <BranchDropdown
                                isFilter={true}
                                selectedBranch={selectedBranch}
                                setSelectedBranch={setSelectedBranch}
                            />
                        </Box>}
                        <Divider orientation="vertical" />

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle1">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }

            {
                isLoading ? <CircularProgress color="primary" size={40} />
                    :
                    <Box>
                        {/* totals row 1 */}
                        <DashboardCard data={data} />

                        {/* row 2 */}
                        <Box
                            sx={{
                                bgcolor: 'white',
                                boxShadow: 1,
                                border: 1,
                                borderColor: theme.palette.customGrey.borderInput,
                                backgroundColor: 'white',
                                borderRadius: 1,
                                width: '100%',
                                mt: 2
                            }}>
                            <PostStatusBarChart postStatuses={data?.postStatuses || []} />
                        </Box>


                        {/* persons row 3 */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                my: 2
                            }}>

                            {/* driver */}
                            <TopThreeCard
                                list={data?.drivers}
                                label={t('topThreeDrivers')}
                                color={theme.palette.dashbaord.red}
                            />

                            <Box width={'2%'} height={isMobile ? 10 : 0}></Box>

                            {/* user */}
                            <TopThreeCard
                                list={data?.users}
                                label={t('topThreeEmployees')}
                                color={theme.palette.dashbaord.blue}
                            />

                            <Box width={'2%'} height={isMobile ? 10 : 0}></Box>

                            {/* page */}
                            <TopThreeCard
                                list={data?.pages}
                                label={t('topThreePages')}
                                color={theme.palette.dashbaord.purple}
                            />
                        </Box>

                        {/* line chart */}
                        <LineChartBox />
                    </Box>


            }
        </Box>
    );
}

export default Dashboard;